import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
// import { fetchProductById } from "../services/productService";
import { fetchSingleProduct } from "../services/productService";
import { useCart } from "../context/CartContext";
import { Helmet } from "react-helmet-async";
/* ─────────────────────────────────────────────
   RAZORPAY INTEGRATION
───────────────────────────────────────────── */
const RAZORPAY_KEY = import.meta.env.VITE_RAZORPAY_KEY_ID || "rzp_test_xxxxxxxxxx";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

async function initiatePurchase({ product, quantity, onSuccess, onError }) {
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    onError?.("Payment gateway failed to load. Please try again.");
    return;
  }

  const totalAmount = product.price * quantity * 100; // Razorpay expects paise

  const options = {
    key: RAZORPAY_KEY,
    amount: totalAmount,
    currency: "INR",
    name: "Sanjivani Ayurveda",
    description: `${product.name} × ${quantity}`,
    image: "/logo.png", // replace with your logo URL
    // order_id: "order_xxxx" — generate server-side for production
    handler: (response) => {
      onSuccess?.(response);
    },
    prefill: {
      name: "",
      email: "",
      contact: "",
    },
    notes: {
      product_id: product.id,
      product_name: product.name,
      quantity,
    },
    theme: { color: "#2d6a4f" },
    modal: {
      ondismiss: () => { },
    },
  };

  const rzp = new window.Razorpay(options);
  rzp.on("payment.failed", (resp) => onError?.(resp.error.description));
  rzp.open();
}

/* ─────────────────────────────────────────────
   STARS COMPONENT
───────────────────────────────────────────── */
const Stars = ({ value, size = "1rem" }) => (
  <span style={{ fontSize: size, letterSpacing: "2px" }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <span key={s} style={{ color: s <= Math.round(value) ? "#d4a017" : "var(--c-border)" }}>★</span>
    ))}
  </span>
);

/* ─────────────────────────────────────────────
   SKELETON LOADER
───────────────────────────────────────────── */
const Skeleton = () => (
  <div className="pd-skeleton">
    <div className="pd-skel-img shimmer" />
    <div className="pd-skel-content">
      {[80, 55, 95, 40, 65, 30].map((w, i) => (
        <div key={i} className="pd-skel-line shimmer" style={{ width: `${w}%`, height: i === 1 ? 40 : i === 3 ? 28 : 14 }} />
      ))}
      <div className="pd-skel-btns">
        <div className="shimmer" style={{ height: 52, borderRadius: 100, flex: 1 }} />
        <div className="shimmer" style={{ height: 52, borderRadius: 100, flex: 1 }} />
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   PRODUCT DETAILS PAGE
───────────────────────────────────────────── */
const TABS = ["Benefits", "Ingredients", "How to Use"];

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem } = useCart();
  const [product, setProduct] = useState(location.state);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("Benefits");
  const [cartAdded, setCartAdded] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [toast, setToast] = useState(null);

  /* Fetch product */
  useEffect(() => {
    window.scrollTo({ top: 0 });


    // If data comes from navigation state
    if (location.state) {
      setProduct(location.state);
      setLoading(false);
      return;
    }

    // Else fallback (API or MOCK)
    (async () => {
      try {
        const data = await fetchSingleProduct(id);
        setProduct(data);
        console.log("Fetched product:", data);
      } catch {

        setError("Product not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, location.state]);

  /* Show toast helper */
  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  /* Add to Cart */
  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      emoji: product.image || "🌿",
      category: product.category || "",
      quantity,                        // ← pass selected quantity
    });
    setCartAdded(true);
    showToast(`${product.name} added to cart!`);
    setTimeout(() => setCartAdded(false), 2400);

  };

  /* Buy Now via Razorpay */
  const handleBuyNow = async () => {
    // setPayLoading(true);
    // await initiatePurchase({
    //   product,
    //   quantity,
    //   onSuccess: (resp) => {
    //     setPayLoading(false);
    //     showToast(`Payment successful! ID: ${resp.razorpay_payment_id}`);
    //   },
    //   onError: (msg) => {
    //     setPayLoading(false);
    //     showToast(msg || "Payment failed. Please try again.", "error");
    //   },
    // });
    // setPayLoading(false);

    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      emoji: product.image || "🌿",
      category: product.category || "",
      quantity,                        // ← pass selected quantity
    });
    setCartAdded(true);
    // showToast(`${product.name} added to cart!`);
    setTimeout(() => setCartAdded(false), 2400);
    navigate('/cart')

  };

  const discount = product?.originalPrice && product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;


  const parseBenefits = (benefitsStr) => {
    if (!benefitsStr) return [];

    return benefitsStr.split("||").map((item) => {
      const [icon, title, desc] = item.split("|");
      return { icon, title, desc };
    });
  };

  const parseIngredients = (ingredientsStr) => {
    if (!ingredientsStr) return [];

    return ingredientsStr.split("||").map((item) => {
      const [name, pct, note] = item.split("|");
      return { name, pct, note };
    });
  };

  const parseUsage = (usageStr) => {
    if (!usageStr) return [];
    return usageStr.split("|"); // jo multiple steps hoy to
  };

  const keywordsMeta = Array.isArray(product?.keywords)
    ? product.keywords.join(", ")
    : product?.keywords || "";

  /* ── RENDER ── */
  return (
    <>
      <Helmet>
        <title>{product?.name} | Ayurvedic Juice | Sanjivani Ayurveda</title>

        <meta name="description" content={product?.description} />

        <meta name="keywords" content={keywordsMeta} />

        <meta property="og:title" content={product?.name} />
        <meta property="og:description" content={product?.description} />
        <meta property="og:image" content={product?.image} />
        <meta property="og:type" content="product" />

        <meta name="twitter:card" content="summary_large_image" />

        {/* Your existing JSON-LD stays */}
      </Helmet>



      <style>{css}</style>

      {/* Toast */}
      {toast && (
        <div className={`pd-toast pd-toast--${toast.type}`}>
          {toast.type === "success" ? "✓" : "✕"} {toast.msg}
        </div>
      )}

      <div className="pd-page">
        {/* Breadcrumb */}
        <div className="pd-breadcrumb">
          <div className="container">
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to="/products">Products</Link>
            {product && (<><span>›</span><span className="pd-breadcrumb__current">{product.name}</span></>)}
          </div>
        </div>

        <div className="container pd-main">

          {/* ── LOADING ── */}
          {loading && <Skeleton />}

          {/* ── ERROR ── */}
          {error && !loading && (
            <div className="pd-error">
              <span>🌿</span>
              <h2>Product Not Found</h2>
              <p>{error}</p>
              <button className="btn-primary" onClick={() => navigate("/products")}>← Back to Products</button>
            </div>
          )}

          {/* ── PRODUCT ── */}
          {!loading && !error && product && (
            <>
              {/* ─── TOP SECTION: Image + Info ─── */}
              <div className="pd-top">

                {/* Image Panel */}
                <div className="pd-image-panel">
                  {product.badge && <span className="pd-badge">{product.badge}</span>}
                  {discount && <span className="pd-disc">-{discount}%</span>}
                  <div className="pd-img-wrap">
                    {product.image ? (
                      <img src={product.image.url} alt={product.name} className="pd-img" />
                    ) : (
                      <div className="pd-img-fallback">{product.emoji}</div>
                    )}
                  </div>
                  {/* Trust badges */}
                  <div className="pd-trust">
                    {(product?.certifications && Array.isArray(product.certifications)
                      ? product.certifications
                      : []
                    ).map((cert) => (
                      <span className="pd-cert" key={cert}>✓ {cert}</span>
                    ))}
                  </div>
                </div>

                {/* Info Panel */}
                <div className="pd-info">
                  <span className="pd-category">{product.category}</span>
                  <h1 className="pd-name">{product.name}</h1>
                  {product.tagline && <p className="pd-tagline">"{product.tagline}"</p>}

                  {/* Rating */}
                  <div className="pd-rating-row">
                    <Stars value={product.rating} />
                    <span className="pd-rating-num">{product.rating}</span>
                    <span className="pd-rating-reviews">({product.reviews} reviews)</span>
                  </div>

                  {/* Meta pills */}
                  <div className="pd-meta-pills">
                    {product.weight && <span className="pd-pill">⚖️ {product.weight}</span>}
                    {product.servings && <span className="pd-pill">🥄 {product.servings}</span>}
                    {product.shelfLife && <span className="pd-pill">📅 {product.shelfLife}</span>}
                  </div>

                  {/* Price */}
                  <div className="pd-price-block">
                    <span className="pd-price">₹{product.price.toLocaleString("en-IN")}</span>
                    {product.originalPrice && (
                      <span className="pd-orig">₹{product.originalPrice.toLocaleString("en-IN")}</span>
                    )}
                    {discount && <span className="pd-save">Save {discount}%</span>}
                  </div>
                  <p className="pd-tax-note">Inclusive of all taxes · Free shipping above ₹499</p>

                  {/* Stock */}
                  <div className={`pd-stock ${product.inStock ? "pd-stock--in" : "pd-stock--out"}`}>
                    <span className="pd-stock__dot" />
                    {product.inStock ? "In Stock — Ships within 24 hours" : "Currently Out of Stock"}
                  </div>

                  {/* Quantity */}
                  {product.inStock && (
                    <div className="pd-qty-row">
                      <span className="pd-qty-label">Qty:</span>
                      <div className="pd-qty">
                        <button
                          className="pd-qty__btn"
                          onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                          disabled={quantity <= 1}
                          aria-label="Decrease quantity"
                        >−</button>
                        <span className="pd-qty__val">{quantity}</span>
                        <button
                          className="pd-qty__btn"
                          onClick={() => setQuantity((q) => Math.min(q + 1))}
                          // disabled={quantity >= 10}
                          aria-label="Increase quantity"
                        >+</button>
                      </div>
                      <span className="pd-qty-total">
                        Total: <strong>₹{(product.price * quantity).toLocaleString("en-IN")}</strong>
                      </span>
                    </div>
                  )}

                  {/* CTAs */}
                  <div className="pd-cta-row">
                    <button
                      className={`pd-btn pd-btn--cart ${cartAdded ? "pd-btn--added" : ""}`}
                      onClick={() => {
                        handleAddToCart()
                      }}
                      disabled={!product.inStock}
                    >
                      {cartAdded ? "✓ Added to Cart!" : "🛒 Add to Cart"}
                    </button>

                    <button
                      className={`pd-btn pd-btn--buy ${payLoading ? "pd-btn--loading" : ""}`}
                      onClick={() => {
                        handleBuyNow()
                      }}
                      disabled={!product.inStock || payLoading}
                    >
                      {payLoading ? (
                        <span className="pd-spinner" />
                      ) : (
                        "⚡ Buy Now"
                      )}
                    </button>
                  </div>

                  {/* Razorpay note */}
                  <p className="pd-pay-note">
                    🔒 Secured by <strong>Razorpay</strong> · UPI, Cards, NetBanking & Wallets accepted
                  </p>

                  {/* Share row */}
                  <div className="pd-share">
                    <span className="pd-share__label">Share:</span>
                    {["WhatsApp", "Facebook", "Copy Link"].map((s) => (
                      <button
                        key={s}
                        className="pd-share__btn"
                        onClick={() => showToast(`${s} sharing coming soon!`)}
                      >{s}</button>
                    ))}
                  </div>
                </div>
              </div>

              {/* ─── TABS SECTION ─── */}
              <div className="pd-tabs-section">
                <div className="pd-tabs">
                  {TABS.map((tab) => (
                    <button
                      key={tab}
                      className={`pd-tab ${activeTab === tab ? "pd-tab--active" : ""}`}
                      onClick={() => setActiveTab(tab)}
                    >{tab}</button>
                  ))}
                </div>

                <div className="pd-tab-content">

                  {/* Benefits */}
                  {activeTab === "Benefits" && (
                    <div className="pd-benefits-grid">
                      {parseBenefits(product?.benefits).map((b, i) => (
                        <div className="pd-benefit" key={i}>
                          <span className="pd-benefit__icon">{b.icon}</span>
                          <div>
                            <h4 className="pd-benefit__title">{b.title}</h4>
                            <p className="pd-benefit__desc">{b.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Ingredients */}
                  {activeTab === "Ingredients" && (
                    <div className="pd-ingredients">
                      <p className="pd-ingredients__intro">
                        Every ingredient is sourced from certified organic farms, verified for potency, and processed without heat degradation.
                      </p>
                      <table className="pd-ing-table">
                        <thead>
                          <tr>
                            <th>Ingredient</th>
                            <th>Amount</th>
                            <th>Function</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseIngredients(product?.ingredients).map((ing, i) => (
                            <tr key={i}>
                              <td className="pd-ing-name">{ing.name}</td>
                              <td className="pd-ing-pct">{ing.pct}</td>
                              <td className="pd-ing-note">{ing.note}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* How to Use */}
                  {activeTab === "How to Use" && (
                    <ol className="pd-usage-list">
                      {parseUsage(product?.usage).map((step, i) => (
                        <li key={i} className="pd-usage-item">
                          <span className="pd-usage-num">{i + 1}</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>

              {/* ─── DESCRIPTION ─── */}
              <div className="pd-desc-section">
                <h2 className="pd-section-title">About This Product</h2>
                <p className="pd-description">{product.description}</p>
              </div>

            </>
          )}
        </div>
      </div >
    </>
  );
}

/* ─────────────────────────────────────────────
   CSS
───────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Jost:wght@300;400;500;600&display=swap');

:root {
  --c-primary:       #2d6a4f;
  --c-primary-light: #40916c;
  --c-primary-dark:  #1b4332;
  --c-accent:        #d4a017;
  --c-bg:            #f9f6f0;
  --c-surface:       #ffffff;
  --c-text:          #1c2b22;
  --c-muted:         #5a7262;
  --c-border:        #d4e6d5;
  --c-error:         #c0392b;
  --f-display:       'Cormorant Garamond', Georgia, serif;
  --f-body:          'Jost', sans-serif;
  --radius:          16px;
  --shadow:          0 4px 24px rgba(27,67,50,.09);
  --shadow-lg:       0 12px 40px rgba(27,67,50,.15);
  --ease:            cubic-bezier(.4,0,.2,1);
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: var(--f-body); background: var(--c-bg); color: var(--c-text); }
.container { max-width: 1160px; margin: 0 auto; padding: 0 24px; }

/* ── BREADCRUMB ── */
.pd-breadcrumb {
  background: var(--c-surface);
  border-bottom: 1px solid var(--c-border);
  padding: 13px 0;
  font-size: .8rem; color: var(--c-muted);
}
.pd-breadcrumb .container { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pd-breadcrumb a { color: var(--c-primary); transition: color .2s; }
.pd-breadcrumb a:hover { color: var(--c-primary-dark); }
.pd-breadcrumb__current { color: var(--c-text); font-weight: 500; }

/* ── PAGE ── */
.pd-page { min-height: 100vh; }
.pd-main { padding: 40px 0 80px; }

/* ── TOAST ── */
.pd-toast {
  position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%);
  font-family: var(--f-body); font-size: .85rem; font-weight: 500;
  padding: 12px 24px; border-radius: 100px;
  z-index: 9999; white-space: nowrap;
  animation: toastIn .3s var(--ease);
  box-shadow: 0 8px 32px rgba(0,0,0,.2);
}
.pd-toast--success { background: var(--c-primary-dark); color: #fff; }
.pd-toast--error   { background: var(--c-error); color: #fff; }
@keyframes toastIn { from { opacity:0; transform: translateX(-50%) translateY(12px); } to { opacity:1; transform: translateX(-50%) translateY(0); } }

/* ── SKELETON ── */
.pd-skeleton {
  display: grid; grid-template-columns: 1fr 1fr; gap: 60px; padding: 40px 0;
}
.pd-skel-img { aspect-ratio:1/1; border-radius:var(--radius); }
.pd-skel-content { display:flex; flex-direction:column; gap:18px; padding-top:12px; }
.pd-skel-line { border-radius:8px; }
.pd-skel-btns { display:flex; gap:14px; margin-top:12px; }
.shimmer {
  background: linear-gradient(90deg,#e8ede9 25%,#d4e0d6 50%,#e8ede9 75%);
  background-size: 200% 100%;
  animation: shim 1.5s infinite;
}
@keyframes shim { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* ── ERROR ── */
.pd-error { text-align:center; padding:80px 24px; }
.pd-error span { font-size:3.5rem; display:block; margin-bottom:16px; }
.pd-error h2 { font-family:var(--f-display); font-size:2rem; color:var(--c-primary-dark); margin-bottom:10px; }
.pd-error p  { color:var(--c-muted); margin-bottom:24px; }
.btn-primary {
  display:inline-flex; align-items:center; font-family:var(--f-body);
  font-size:.82rem; font-weight:600; letter-spacing:.07em; text-transform:uppercase;
  padding:12px 28px; border-radius:100px; border:none;
  background:var(--c-primary); color:#fff; cursor:pointer;
  transition:background .22s, transform .22s;
}
.btn-primary:hover { background:var(--c-primary-dark); transform:translateY(-1px); }

/* ══════════════════════════════════
   TOP SECTION
══════════════════════════════════ */
.pd-top {
  display: grid; grid-template-columns: 1fr 1.1fr;
  gap: 60px; align-items: start;
  margin-bottom: 56px;
}

/* ── Image Panel ── */
.pd-image-panel {
  position: sticky; top: 24px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  padding: 32px;
  position: relative;
  animation: fadeLeft .6s var(--ease) both;
}
@keyframes fadeLeft { from{opacity:0;transform:translateX(-24px)} to{opacity:1;transform:none} }

.pd-badge {
  position:absolute; top:16px; left:16px;
  background:var(--c-primary); color:#fff;
  font-size:.62rem; font-weight:700; letter-spacing:.08em; text-transform:uppercase;
  padding:4px 12px; border-radius:100px; z-index:2;
}
.pd-disc {
  position:absolute; top:16px; right:16px;
  background:#e53e3e; color:#fff;
  font-size:.62rem; font-weight:700;
  padding:4px 10px; border-radius:100px; z-index:2;
}
.pd-img-wrap {
  width:100%; aspect-ratio:1/1; overflow:hidden; border-radius:12px;
  background:linear-gradient(135deg,#f0f7f2,#e8f5ec);
  display:flex; align-items:center; justify-content:center;
  margin-bottom:24px;
}
.pd-img {    width: fit-content;  height:100%; object-fit:cover; transition:transform .4s var(--ease); }
.pd-image-panel:hover .pd-img { transform:scale(1.04); }
.pd-img-fallback { font-size:8rem; transition:transform .4s var(--ease); }
.pd-image-panel:hover .pd-img-fallback { transform:scale(1.08) rotate(-4deg); }

.pd-trust { display:flex; flex-wrap:wrap; gap:8px; }
.pd-cert {
  font-size:.7rem; font-weight:600; color:var(--c-primary);
  background:rgba(45,106,79,.08); border:1px solid var(--c-border);
  padding:4px 12px; border-radius:100px; letter-spacing:.04em;
}

/* ── Info Panel ── */
.pd-info { animation:fadeRight .6s var(--ease) both; }
@keyframes fadeRight { from{opacity:0;transform:translateX(24px)} to{opacity:1;transform:none} }

.pd-category {
  display:inline-block; font-size:.7rem; font-weight:600;
  letter-spacing:.14em; text-transform:uppercase;
  color:var(--c-primary-light); margin-bottom:10px;
}
.pd-name {
  font-family:var(--f-display); font-size:clamp(2rem,3.5vw,3rem);
  font-weight:600; color:var(--c-primary-dark); line-height:1.15;
  margin-bottom:8px;
}
.pd-tagline {
  font-family:var(--f-display); font-style:italic;
  font-size:1.05rem; color:var(--c-muted); margin-bottom:18px;
}

.pd-rating-row {
  display:flex; align-items:center; gap:10px; margin-bottom:18px;
}
.pd-rating-num { font-weight:700; font-size:.95rem; color:var(--c-accent); }
.pd-rating-reviews { font-size:.82rem; color:var(--c-muted); }

.pd-meta-pills { display:flex; gap:9px; flex-wrap:wrap; margin-bottom:24px; }
.pd-pill {
  font-size:.76rem; font-weight:500;
  background:var(--c-bg); border:1px solid var(--c-border);
  color:var(--c-muted); padding:5px 14px; border-radius:100px;
}

/* Price */
.pd-price-block {
  display:flex; align-items:baseline; gap:12px; margin-bottom:6px;
}
.pd-price {
 font-size:1.6rem;
  font-weight:600;
  color:var(--c-primary-dark);
   line-height:1;
}
.pd-orig { font-size:1.1rem; color:var(--c-muted); text-decoration:line-through; }
.pd-save {
  font-size:.75rem; font-weight:700; letter-spacing:.06em; text-transform:uppercase;
  background:#fef3c7; color:#92400e; padding:4px 10px; border-radius:100px;
}
.pd-tax-note { font-size:.76rem; color:var(--c-muted); margin-bottom:18px; }

/* Stock */
.pd-stock {
  display:inline-flex; align-items:center; gap:8px;
  font-size:.8rem; font-weight:600; letter-spacing:.04em;
  padding:7px 16px; border-radius:100px; margin-bottom:22px;
}
.pd-stock--in  { background:rgba(45,106,79,.08); color:var(--c-primary); }
.pd-stock--out { background:rgba(192,57,43,.08); color:var(--c-error); }
.pd-stock__dot {
  width:8px; height:8px; border-radius:50%; flex-shrink:0;
}
.pd-stock--in  .pd-stock__dot { background:var(--c-primary); box-shadow:0 0 6px rgba(45,106,79,.6); animation:pulse 2s infinite; }
.pd-stock--out .pd-stock__dot { background:var(--c-error); }
@keyframes pulse { 0%,100%{box-shadow:0 0 4px rgba(45,106,79,.5)} 50%{box-shadow:0 0 10px rgba(45,106,79,.9)} }

/* Quantity */
.pd-qty-row { display:flex; align-items:center; gap:16px; margin-bottom:24px; flex-wrap:wrap; }
.pd-qty-label { font-size:.82rem; font-weight:600; color:var(--c-muted); }
.pd-qty {
  display:flex; align-items:center;
  border:1.5px solid var(--c-border); border-radius:100px;
  overflow:hidden;
}
.pd-qty__btn {
  width:38px; height:38px; background:var(--c-bg); border:none;
  font-size:1.2rem; cursor:pointer; color:var(--c-primary-dark);
  transition:background .2s; display:flex; align-items:center; justify-content:center;
}
.pd-qty__btn:hover:not(:disabled) { background:var(--c-border); }
.pd-qty__btn:disabled { color:var(--c-border); cursor:not-allowed; }
.pd-qty__val {
  width:42px; text-align:center; font-weight:600;
  font-size:.95rem; color:var(--c-text);
}
.pd-qty-total { font-size:.88rem; color:var(--c-muted); }
.pd-qty-total strong { color:var(--c-primary-dark); }

/* CTAs */
.pd-cta-row { display:flex; gap:14px; margin-bottom:14px; flex-wrap:wrap; }
.pd-btn {
  flex:1; min-width:140px;
  font-family:var(--f-body); font-size:.85rem; font-weight:600;
  letter-spacing:.06em; text-transform:uppercase;
  padding:15px 24px; border-radius:100px; border:none; cursor:pointer;
  display:flex; align-items:center; justify-content:center; gap:8px;
  transition:transform .22s var(--ease), box-shadow .22s, background .22s;
}
.pd-btn--cart {
  background:transparent; color:var(--c-primary);
  border:2px solid var(--c-primary);
}
.pd-btn--cart:hover:not(:disabled) {
  background:var(--c-primary); color:#fff;
  transform:translateY(-2px);
  box-shadow:0 6px 20px rgba(45,106,79,.28);
}
.pd-btn--added { background:var(--c-primary) !important; color:#fff !important; }

.pd-btn--buy {
  background:var(--c-primary); color:#fff;
  box-shadow:0 4px 16px rgba(45,106,79,.3);
}
.pd-btn--buy:hover:not(:disabled) {
  background:var(--c-primary-dark);
  transform:translateY(-2px);
  box-shadow:0 8px 28px rgba(45,106,79,.38);
}
.pd-btn--loading { opacity:.75; cursor:wait; }
.pd-btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }

.pd-spinner {
  width:18px; height:18px; border-radius:50%;
  border:2.5px solid rgba(255,255,255,.35);
  border-top-color:#fff;
  animation:spin .7s linear infinite;
}
@keyframes spin { to{transform:rotate(360deg)} }

.pd-pay-note {
  font-size:.75rem; color:var(--c-muted); margin-bottom:20px; text-align:center;
}

/* Share */
.pd-share { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.pd-share__label { font-size:.78rem; color:var(--c-muted); font-weight:500; }
.pd-share__btn {
  font-family:var(--f-body); font-size:.74rem; font-weight:500;
  padding:6px 14px; border-radius:100px;
  border:1px solid var(--c-border); background:var(--c-surface);
  color:var(--c-muted); cursor:pointer; transition:all .2s;
}
.pd-share__btn:hover { border-color:var(--c-primary); color:var(--c-primary); }

/* ══════════════════════════════════
   TABS
══════════════════════════════════ */
.pd-tabs-section {
  background:var(--c-surface);
  border:1px solid var(--c-border);
  border-radius:var(--radius);
  overflow:hidden; margin-bottom:40px;
  animation:fadeUp .6s var(--ease) .2s both;
}
@keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }

.pd-tabs {
  display:flex; border-bottom:1px solid var(--c-border);
  background:var(--c-bg);
}
.pd-tab {
  flex:1; padding:16px 20px; background:none; border:none;
  font-family:var(--f-body); font-size:.84rem; font-weight:600;
  letter-spacing:.05em; text-transform:uppercase;
  color:var(--c-muted); cursor:pointer;
  transition:color .22s, border-color .22s;
  border-bottom:3px solid transparent;
  position:relative;
}
.pd-tab:hover { color:var(--c-primary); }
.pd-tab--active {
  color:var(--c-primary-dark);
  border-bottom-color:var(--c-primary);
  background:var(--c-surface);
}

.pd-tab-content { padding:36px 40px; }

/* Benefits grid */
.pd-benefits-grid { display:grid; grid-template-columns:1fr 1fr; gap:24px; }
.pd-benefit {
  display:flex; gap:16px; align-items:flex-start;
  padding:20px; background:var(--c-bg);
  border:1px solid var(--c-border); border-radius:12px;
  transition:transform .24s var(--ease), box-shadow .24s;
}
.pd-benefit:hover { transform:translateY(-3px); box-shadow:var(--shadow); }
.pd-benefit__icon { font-size:1.8rem; flex-shrink:0; line-height:1; }
.pd-benefit__title { font-family:var(--f-display); font-size:1.1rem; font-weight:600; color:var(--c-primary-dark); margin-bottom:5px; }
.pd-benefit__desc  { font-size:.85rem; color:var(--c-muted); font-weight:300; line-height:1.65; }

/* Ingredients table */
.pd-ingredients__intro { font-size:.92rem; color:var(--c-muted); font-weight:300; line-height:1.7; margin-bottom:24px; }
.pd-ing-table { width:100%; border-collapse:collapse; font-size:.88rem; }
.pd-ing-table th {
  text-align:left; font-size:.7rem; font-weight:700;
  letter-spacing:.12em; text-transform:uppercase;
  color:var(--c-primary-light); padding:10px 16px;
  background:var(--c-bg); border-bottom:2px solid var(--c-border);
}
.pd-ing-table td { padding:14px 16px; border-bottom:1px solid var(--c-border); vertical-align:top; }
.pd-ing-table tr:last-child td { border-bottom:none; }
.pd-ing-table tr:hover td { background:rgba(45,106,79,.03); }
.pd-ing-name { font-weight:500; color:var(--c-text); font-size:.9rem; }
.pd-ing-pct  { font-weight:700; color:var(--c-primary); white-space:nowrap; }
.pd-ing-note { color:var(--c-muted); font-weight:300; }

/* Usage list */
.pd-usage-list { list-style:none; display:flex; flex-direction:column; gap:16px; }
.pd-usage-item { display:flex; align-items:flex-start; gap:16px; font-size:.92rem; color:var(--c-text); line-height:1.65; }
.pd-usage-num {
  flex-shrink:0; width:30px; height:30px; border-radius:50%;
  background:var(--c-primary); color:#fff;
  font-size:.78rem; font-weight:700;
  display:flex; align-items:center; justify-content:center;
  margin-top:1px;
}

/* ── DESCRIPTION ── */
.pd-desc-section {
  background:var(--c-surface); border:1px solid var(--c-border);
  border-radius:var(--radius); padding:40px;
  animation:fadeUp .6s var(--ease) .35s both;
}
.pd-section-title {
  font-family:var(--f-display); font-size:1.8rem; font-weight:600;
  color:var(--c-primary-dark); margin-bottom:18px;
}
.pd-description { font-size:.97rem; color:var(--c-muted); font-weight:300; line-height:1.85; }

/* ══════════════════════════════════
   RESPONSIVE
══════════════════════════════════ */
@media (max-width: 900px) {
  .pd-top { grid-template-columns:1fr; gap:32px; }
  .pd-image-panel { position:static; }
  .pd-benefits-grid { grid-template-columns:1fr; }
}
@media (max-width: 640px) {
  .pd-page { padding: 0 20px }
  .pd-tab-content { padding:24px 20px; }
  .pd-cta-row { flex-direction:column; }
  .pd-btn { min-width:unset; }
  .pd-skeleton { grid-template-columns:1fr; }
  // .pd-tabs { flex-direction:column; }
  .pd-tabs { flex-wrap:wrap }
  .pd-tab { text-align:left; border-bottom:none; border-left:3px solid transparent; padding-left:10px; padding-right:0px }
  .pd-tab--active { border-left-color:var(--c-primary); border-bottom-color:transparent; }
}
`;
